import React from 'react';
import PropTypes from 'prop-types';

const fixIE11Transform = ref => {
  if (global.window && ref) {
    const transform = getComputedStyle(ref).getPropertyValue('transform');
    ref.setAttribute('transform', transform);
  }
};

class SimpleAxis extends React.Component {
  // eslint-disable-line

  static defaultProps = {
    scaleFunc: null,
    ticks: null,
    axis: 'x',
    tickClass: null,
    lineClass: null,
    labelClass: null,
    flipAlign: false,
    skipExtent: false,
    tickSize: 5,
    labelPadding: 5,
    labelRotation: 0,
    labelPosition: 'centered',
    labelFormat: null,
    lineStyle: null,
    labelStyle: {},
    tickStyle: null,
  };

  static propTypes = {
    scaleFunc: PropTypes.func,
    ticks: PropTypes.array,
    axis: PropTypes.oneOf(['x', 'y']),
    tickClass: PropTypes.string,
    lineClass: PropTypes.string,
    labelClass: PropTypes.string,
    flipAlign: PropTypes.bool,
    skipExtent: PropTypes.bool,
    tickStyle: PropTypes.object,
    lineStyle: PropTypes.object,
    labelStyle: PropTypes.object,
    tickSize: PropTypes.number,
    labelPadding: PropTypes.number,
    labelRotation: PropTypes.number,
    labelFormat: PropTypes.func,
    labelPosition: PropTypes.oneOf(['inside', 'centered']),
    // scaleType: PropTypes.oneOf(['banded', 'ordinal', 'linear']);
  };

  render() {
    const {
      scaleFunc,
      ticks,
      axis,
      skipExtent,
      flipAlign,
      lineStyle,
      tickStyle,
      labelStyle,
      lineClass,
      tickClass,
      labelClass,
      tickSize,
      labelPadding,
      labelRotation,
      labelFormat,
      labelPosition,
      ...otherProps
    } = this.props;
    if (scaleFunc === undefined) return null;

    let tickSign = 1;
    if (axis === 'x') tickSign = flipAlign ? -1 : 1;
    if (axis === 'y') tickSign = flipAlign ? 1 : -1;

    const tickLength = tickSize * tickSign;
    const labelOffset = labelPadding * tickSign;

    const isBanded =
      scaleFunc.padding &&
      scaleFunc.paddingInner &&
      scaleFunc.paddingOuter &&
      scaleFunc.bandwidth;

    const tickValues =
      ticks || (isBanded ? scaleFunc.domain() : scaleFunc.ticks());

    const tickPositions = tickValues.map(t => scaleFunc(t));

    // generate the positions for the tick lines
    const tickCompProps = tickPositions
      // if the scale is banded, positions need half the of the bandwidth as offset
      .map(t => (isBanded ? t + scaleFunc.bandwidth() / 2 : t)) // eslint-disable-line
      .map(t => {
        if (axis === 'x') {
          return {
            x1: t,
            x2: t,
            y1: 0,
            y2: tickLength,
          };
        }
        return {
          x1: 0,
          x2: tickLength,
          y1: t,
          y2: t,
        };
      });

    // const optionalRemoveExtent

    // generate the positions for the labels
    const labelProps = tickPositions
      // if the scale is banded, positions need half the of the bandwidth as offset
      .map(t => (isBanded ? t + scaleFunc.bandwidth() / 2 : t)) // eslint-disable-line
      .map((t, i, arr) => {
        if (axis === 'x') {
          let anchor = 'middle';
          if (labelRotation < 0) anchor = flipAlign ? 'start' : 'end';
          if (labelRotation > 0) anchor = flipAlign ? 'end' : 'start';
          if (labelPosition === 'inside' && i === 0) anchor = 'start';
          if (labelPosition === 'inside' && i === arr.length - 1)
            anchor = 'end';
          return {
            anchor,
            position: {
              x: t,
              y: tickLength + labelOffset,
            },
            baseline: flipAlign ? 'no-change' : 'hanging',
          };
        }
        const anchor = flipAlign ? 'start' : 'end';
        let baseline = 'middle';
        if (labelPosition === 'inside' && i === 0) baseline = 'hanging';
        if (labelPosition === 'inside' && i === arr.length - 1)
          baseline = 'no-change';
        return {
          anchor,
          position: {
            x: tickLength + labelOffset,
            y: t,
          },
          baseline,
        };
      });

    const rangeValues = scaleFunc.range();
    const range = [rangeValues[0], rangeValues[rangeValues.length - 1]];

    const baseLineProps =
      axis === 'x'
        ? { x1: range[0], x2: range[1], y1: 0, y2: 0 }
        : { x1: 0, x2: 0, y1: range[0], y2: range[1] };

    // create the bounding ticks for the scale
    const baseLineTickProps =
      axis === 'x'
        ? [
            { x1: range[0], x2: range[0], y1: 0, y2: tickLength }, // left tick
            { x1: range[1], x2: range[1], y1: 0, y2: tickLength }, // right tick
          ]
        : [
            { x1: 0, x2: tickLength, y1: range[0], y2: range[0] }, // bottom tick
            { x1: 0, x2: tickLength, y1: range[1], y2: range[1] }, // top tick
          ];

    return (
      <g {...otherProps} ref={fixIE11Transform}>
        <line
          {...baseLineProps}
          style={{ ...lineStyle }}
          className={lineClass}
        />
        {!skipExtent && (
          <line
            {...baseLineTickProps[0]}
            style={{ ...lineStyle }}
            className={lineClass}
          />
        )}
        {!skipExtent && (
          <line
            {...baseLineTickProps[1]}
            style={{ ...lineStyle }}
            className={lineClass}
          />
        )}
        <g>
          {labelProps.map((l, i) => (
            <text
              ref={fixIE11Transform}
              key={`${tickValues[i]}-${
                axis === 'x' ? l.position.x : l.position.y
              }`}
              className={labelClass}
              textAnchor={l.anchor}
              dominantBaseline={l.baseline}
              style={{
                dominantBaseline: l.baseline,
                transform: `translate(${l.position.x}px, ${l.position.y}px)rotate(${labelRotation}deg)`,
                ...labelStyle,
              }}
            >
              {labelFormat ? labelFormat(tickValues[i], i) : tickValues[i]}
            </text>
          ))}
        </g>
        <g>
          {tickCompProps.map((t, i) => (
            <line
              {...t}
              key={tickValues[i]}
              style={{ ...tickStyle }}
              className={tickClass}
            />
          ))}
        </g>
      </g>
    );
  }
}

export default SimpleAxis;
